import {Component, OnInit} from '@angular/core';

import {TranslocoPipe, TranslocoService} from "@jsverse/transloco";
import {CreateServiceModal} from '../../components/services/create-service-modal/create-service-modal';
import {Service} from '../../../core/entities/service/service.entity';
import {ServiceService} from '../../services/service/service.service';
import {ToastrService} from 'ngx-toastr';
import {ServiceItemComponent} from '../../components/services/service-item/service-item.component';
import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop';
import { Subject, debounceTime } from 'rxjs';

@Component({
  selector: 'app-services',
  imports: [
    TranslocoPipe,
    CreateServiceModal,
    ServiceItemComponent,
    DragDropModule
],
  templateUrl: './services.component.html',
  styleUrl: './services.component.scss'
})
export class ServicesComponent implements OnInit {
  public loadingServices = false;
  public services: Service[] = [];
  public createServiceModalOpened = false;
  public serviceToEdit: Service|null = null;

  private orderChanged$ = new Subject<void>();

  public constructor(
    private readonly serviceService: ServiceService,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService,
  ) {
  }

  public ngOnInit() {
    this.loadingServices = true;
    this.serviceService.getServices().subscribe(
      (services) => {
        this.services = this.sortServices(services);
        this.loadingServices = false;
      },
      () => {
        this.toastr.error(
          this.transloco.translate('services.error.retrieving-services.content'),
          this.transloco.translate('services.error.retrieving-services.title')
        )
      }
    );

    this.orderChanged$
      .pipe(debounceTime(1000)) // delay before updating the order
      .subscribe(() => this.saveOrder());
  }

  public openCreateServiceModal(): void {
    this.createServiceModalOpened = true;
  }

  public closeCreateServiceModal(service: Service | null = null): void {
    if (service !== null) {
      const index = this.services.findIndex(s => s.id === service.id);

      if (index === -1) {
        this.services = this.sortServices([service, ...this.services]);
      } else {
        const updatedServices = [...this.services];
        updatedServices[index] = service;
        this.services = this.sortServices(updatedServices);
      }
    }

    this.serviceToEdit = null;
    this.createServiceModalOpened = false;
  }

  public onServiceDeleted(service: Service | null = null): void {
    if (service === null) {
      return;
    }
    const index = this.services.findIndex(s => s.id === service.id);
    if (index !== -1) {
      this.services.splice(index, 1);
    }
  }

  public drop(event: CdkDragDrop<Service[]>): void {
    moveItemInArray(this.services, event.previousIndex, event.currentIndex);
    this.syncServicePositions();
    this.orderChanged$.next();
  }

  public saveOrder(): void {
    const payload = this.services.map((service) => ({
      id: service.id,
      position: service.position ?? 0
    }));

    this.serviceService.updateOrder(payload).subscribe({
      next: () => this.toastr.success(
        this.transloco.translate('services.order.success.content'),
        this.transloco.translate('services.order.success.title')
      ),
      error: () => this.toastr.error(
        this.transloco.translate('services.order.error.content'),
        this.transloco.translate('services.order.error.title')
      )
    });
  }

  public onEditService(service: Service): void {
    this.serviceToEdit = service;
    this.createServiceModalOpened = true;
  }

  private sortServices(services: Service[]): Service[] {
    const getPosition = (service: Service) => service.position ?? Number.MAX_SAFE_INTEGER;
    return [...services].sort((a, b) => getPosition(a) - getPosition(b));
  }

  private syncServicePositions(): void {
    this.services = this.services.map((service, index) => ({
      ...service,
      position: index
    }));
  }
}
